import * as React from 'react'
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFormikContext } from 'formik'

import { TextField } from './atoms'
import { useGetError } from './useGetError'
import type {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from './typings'
import { useHideOptional } from './useHideOptional'
import debounce from '@mui/material/utils/debounce'
import { Theme } from '@mui/material/styles'

export function EditableCustomNumber(props: EditableCustomFieldProps) {
  const {
    margin,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    textFieldProps,
    whiteBackground = false,
    avoidValidateOnBlur = false,
  } = props

  const {
    handleBlur,
    setFieldValue,
    initialValues: {
      customFields: { number },
    },
  } = useFormikContext<ICustomFieldValueFormModel>()

  const fieldName = `customFields.number[${fieldId}]`
  const initialFieldValue = number[fieldId]?.value ?? ''

  const [value, setValue] = useState(initialFieldValue)

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value
      // remove everything but digits, decimal point, negative sign
      const numeric = raw.replace(/[^\d\-.]/g, '')
      setValue(numeric)
    },
    [],
  )

  const update = useMemo(
    () =>
      debounce(
        (v: string | null) => setFieldValue(`${fieldName}.value`, v),
        200,
      ),
    [fieldName, setFieldValue],
  )

  useEffect(() => {
    update.clear()
    update(value)
  }, [update, value])

  useEffect(() => {
    setValue(initialFieldValue)
  }, [initialFieldValue])

  const { hasError, errorText } = useGetError(props)

  const digitCount = value.replace(/[^\d]/g, '').length

  const helperText = hasError ? errorText : `${digitCount}/16 digits`

  const showField = useHideOptional(props)

  if (!showField) return null

  return (
    <TextField
      fullWidth
      label={name}
      value={value}
      margin={margin}
      name={fieldName}
      error={hasError}
      required={required}
      onChange={onChange}
      onBlur={!avoidValidateOnBlur ? handleBlur : undefined}
      helperText={helperText}
      InputProps={{
        sx: {
          backgroundColor: whiteBackground
            ? (theme: Theme) => theme.palette.common.white
            : 'inherit',
        },
      }}
      {...textFieldProps}
      data-test-id={`custom-number-field-${fieldId}`}
    />
  )
}
